import {
  PropsWithChildren,
  ReactNode,
  Children,
  cloneElement,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useMeasure } from 'react-use';
import { noop, sumBy } from 'lodash-es';
import { Chip, Tooltip } from '@ds';

type TruncatedListWrapperProps = PropsWithChildren<{
  wrapperClassName?: string;
  restRenderer?: (hiddenChildren: ReactNode[]) => ReactNode;
}>;

const defaultRestRenderer = (hiddenChildren: ReactNode[]): ReactNode => (
  <Tooltip content={<div className="flex flex-col gap-2">{hiddenChildren}</div>}>
    <Chip size="sm">+{hiddenChildren.length}</Chip>
  </Tooltip>
);

const getParentGap = (el: HTMLElement | null) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const gapVal = style.columnGap || style.gap || '0';
  const parsed = parseFloat(gapVal);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const TruncatedListWrapper = ({
  wrapperClassName,
  children,
  restRenderer = defaultRestRenderer,
}: TruncatedListWrapperProps) => {
  const childArray = Children.toArray(children);
  const childRefs = useRef<(HTMLElement | null)[]>([]);
  const [restItemRef, { width: restItemWidth }] = useMeasure<HTMLDivElement>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const measure = useCallback(() => {
    const refs = childRefs.current.filter(Boolean) as HTMLElement[];
    if (!refs.length) return;
    const wrapperWidth = wrapperRef.current?.clientWidth ?? 0;
    const gap = getParentGap(wrapperRef.current);
    let requiredTotalWidth = sumBy(refs, 'clientWidth') + gap * (refs.length - 1);
    const calcWithRest = refs.length < childArray.length;
    const currentTotalWidth = calcWithRest ? requiredTotalWidth + restItemWidth + gap : requiredTotalWidth;

    if (currentTotalWidth <= wrapperWidth) {
      setVisibleCount(refs.length);
      return;
    }

    for (let i = refs.length - 1; i >= 0; i -= 1) {
      const el = refs[i];
      const w = el.clientWidth;
      const extra = i === 0 ? 0 : gap;
      requiredTotalWidth -= w + extra;
      if (requiredTotalWidth + restItemWidth + gap <= wrapperWidth) {
        setVisibleCount(i);
        return;
      }
    }

    setVisibleCount(0);
  }, [childArray, restItemWidth]);

  useLayoutEffect(() => {
    measure();
    if (!wrapperRef.current) return noop;
    const resizeObs = new ResizeObserver(measure);
    resizeObs.observe(wrapperRef.current);
    return () => resizeObs?.disconnect();
  }, [measure]);

  const visibleChildren = useMemo(() => {
    return childArray.slice(0, visibleCount ?? childArray.length).map((child, i) =>
      // eslint-disable-next-line no-return-assign
      cloneElement(child as never, { ref: (el: HTMLElement) => (childRefs.current[i] = el) }),
    );
  }, [childArray, visibleCount]);

  const restItem = useMemo(() => {
    const hiddenChildren = childArray.slice(visibleCount ?? childArray.length);
    if (!hiddenChildren.length) return null;

    return <div ref={restItemRef}>{restRenderer(hiddenChildren)}</div>;
  }, [childArray, restItemRef, restRenderer, visibleCount]);

  return (
    <div ref={wrapperRef} className={wrapperClassName}>
      {visibleChildren}
      {restItem}
    </div>
  );
};
